import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type NetworkedGroupJSON = z.infer<typeof NetworkedGroupSchema.NetworkedGroupJSONZ>;
export type NetworkedGroupParams = z.infer<typeof NetworkedGroupSchema.NetworkedGroupZ>;

export default class NetworkedGroupSchema {
  public static readonly NetworkedGroupJSONZ = z.object({
    id: z.uuid(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    memberUserIds: z.array(z.uuid()),
    created: z.iso.datetime()
  });

  public static readonly NetworkedGroupZ = z.object({
    id: z.instanceof(Guid),
    name: z.string().optional(),
    description: z.string().optional(),
    memberUserIds: z.array(z.instanceof(Guid)),
    created: z.instanceof(AHDate)
  });

  public static validate(values: unknown): NetworkedGroupParams {
    return parseOrThrow(NetworkedGroupSchema.NetworkedGroupZ, values, {
      logError: true,
      schemaName: "NetworkedGroupSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): NetworkedGroupJSON {
    return parseOrThrow(NetworkedGroupSchema.NetworkedGroupJSONZ, json, {
      logError: true,
      schemaName: "NetworkedGroupSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is NetworkedGroupParams {
    try {
      NetworkedGroupSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is NetworkedGroupJSON {
    try {
      NetworkedGroupSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: NetworkedGroupJSON): NetworkedGroupParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(NetworkedGroupSchema.NetworkedGroupJSONZ, json, {
      logError: true,
      schemaName: "NetworkedGroupSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return NetworkedGroupSchema.codec.decode(parsed);
  }

  public static serialize(params: NetworkedGroupParams): NetworkedGroupJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = NetworkedGroupSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return NetworkedGroupSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(NetworkedGroupSchema.NetworkedGroupJSONZ, NetworkedGroupSchema.NetworkedGroupZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      name: data.name ?? undefined,
      description: data.description ?? undefined,
      memberUserIds: data.memberUserIds.map((id: string) => Guid.fromJSON(id)!),
      created: new AHDate(data.created)
    }),
    encode: (group) => ({
      id: group.id.toJSON(),
      name: group.name,
      description: group.description,
      memberUserIds: group.memberUserIds.map((id: Guid) => id.toJSON()),
      created: group.created.toJSON()
    }),
  });
}
